import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { useDropzone } from 'react-dropzone';
import { CSS } from '@dnd-kit/utilities';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import SkillsInput from "../common/SkillsInput";
import CloseIcon from '@mui/icons-material/Close';
import QuestionPopupForm from './QuestionPopupForm';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    useSortable,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
    Box, TextField, Button, Typography, Snackbar, Alert as MuiAlert, CircularProgress, FormControl, FormControlLabel,
    RadioGroup, Radio, Checkbox, Divider, Tab, Tabs, FormHelperText, DialogTitle, DialogContent, Dialog, Paper,
    IconButton
} from '@mui/material';

import {
    //Save as SaveIcon,
    ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { quizService } from '../../services/api';
const FormSection = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(4)
}));
const dialogContentStyle = {
    '&::-webkit-scrollbar': {
        width: '6px'
    },
    '&::-webkit-scrollbar-track': {
        background: '#f1f1f1'
    },
    '&::-webkit-scrollbar-thumb': {
        background: '#888',
        borderRadius: '4px'
    }
};
const modules = {
    toolbar: {
        container: [
            [{ 'header': '1' }, { 'header': '2' }, { 'font': [] }],
            [{ size: [] }],
            ['bold', 'italic', 'underline', 'strike', 'blockquote'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'indent': '-1' }, { 'indent': '+1' }],
            ['clean']
        ]
    },
    clipboard: { matchVisual: false }
};


/**
 * QuizForm is a React component for creating and editing quiz.
 * It displays a form with fields for entering quiz name, description.
 
 * @returns {React.ReactElement} The rendered form.
 */
const QuizForm = () => {
    var { id } = useParams();
    const navigate = useNavigate();
    const isEditMode = !!id;

    const [formData, setFormData] = useState({
        quiz_id: '',
        quiz_title: '',
        description: '',
        skills: []

    });
    const [showAnswers, setShowAnswers] = useState(false);
    const [showResultOption, setShowResultOption] = useState('');
    const [gradeType, setGradeType] = useState('');
    const [markPerQuestion, setMarkPerQuestion] = useState('')
    const [loading, setLoading] = useState(false);
    const [fetchLoading, setFetchLoading] = useState(false);
    const [timeLimitEnabled, setTimeLimitEnabled] = useState(false);
    const [timeLimit, setTimeLimit] = useState('');
    const [shuffleAnswers, setShuffleAnswers] = useState(false);
    const [randomizeQuestions, setRandomizeQuestion] = useState(false);
    const [tabValue, setTabValue] = useState(0);
    const [activeTab, setActiveTab] = useState(0);
    const [show, setShow] = useState(false);
    const [quizQuestions, setQuizQuestions] = useState([]);
    const [questionToRemove, setQuestionToRemove] = useState(null);
    const [deletingId, setDeletingId] = useState(null);
    const [passPercentage, setPassPercentage] = useState('');
    const [isSaved, setIsSaved] = useState(false);
    const [errors, setErrors] = useState({});
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });
    const [tabComponents, setTabComponents] = useState({
        0: null, // QuizForm
        1: null,

    });

    const handleShow = () => {
        setShow(true);
    };

    const handleClose = () => {
        setShow(false);
    };

    useEffect(() => {
        if (isEditMode) {
            fetchQuiz();
        }
    }, [id]);

    const fetchQuiz = async () => {
        setFetchLoading(true);
        try {
            const response = await quizService.getQuiz(id);
            if (response.data) {
                const quiz = response.data.data;
                const selected_questions = response.data.quiz_questions;
                setFormData({
                    quiz_id: quiz.id,
                    quiz_title: quiz.title || '',
                    description: quiz.description || '',
                    skills: quiz.skills || [],
                });
                setShowAnswers(quiz.show_ans === '1');
                setShowResultOption(quiz.answers_pass_fail); // '1' or '2'
                setGradeType(Number(quiz.grade_type));
                setMarkPerQuestion(quiz.mark_per_question || '');
                setTimeLimitEnabled(quiz.time_limit_req === 1);
                setTimeLimit(quiz.time_limit || '');
                setShuffleAnswers(quiz.shuffle_answers === 1);
                setRandomizeQuestion(quiz.random_questions === 1);
                setQuizQuestions(selected_questions);
                setPassPercentage(quiz.pass_percentage ? String(quiz.pass_percentage) : '');
            }
        } catch (error) {
            console.error('Error fetching quiz:', error);
            setSnackbar({
                open: true,
                message: 'Failed to load quiz data',
                severity: 'error'
            });
        } finally {
            setFetchLoading(false);
        }
    };

    const handleTabChange = (event, newValue) => {
        if (newValue === 1) {
            if (!isEditMode && !isSaved) {
                setSnackbar({
                    open: true,
                    message: 'Please save the quiz before accessing the Questions tab.',
                    severity: 'error',
                });
                return;
                }
            if (validateForm()) {
                setActiveTab(newValue); // Ensure this updates the activeTab
                setTabValue(newValue);
                window.history.pushState({}, '', `/quiz/form/${formData.quiz_id}?tab=1`);
            } else {
                setSnackbar({
                    open: true,
                    message: 'Please fill all required fields in the Details section before proceeding to Questions.',
                    severity: 'error'
                });
            }
        } else if (newValue === 0) {
            setActiveTab(newValue); // Ensure this updates the activeTab
            setTabValue(newValue);
            window.history.pushState({}, '', `/quiz/form/${formData.quiz_id}`); // Change URL to "Details" tab
        }
    };


    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Clear error when user types
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: null
            }));
        }
    };

    const handleDescriptionChange = (content) => {
        setFormData(prev => ({
            ...prev,
            description: content
        }));
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.quiz_title.trim()) {
            newErrors.quiz_title = 'Quiz name is required';
        }
        if (!formData.skills || formData.skills.length === 0) {
            newErrors.skills = 'At least one skill is required';
        }
        if (timeLimitEnabled && !timeLimit) {
            newErrors.timeLimit = 'Time limit is required';
        }

        if (gradeType === 0 && !markPerQuestion) {
            newErrors.markPerQuestion = 'Point per question is required when Auto Grade is selected';
        }
        if (gradeType !== 0 && gradeType !== 1) {
            newErrors.gradeType = 'Please select either Auto Grade or Manual Grade';
        }

        if (!passPercentage) {
            newErrors.passPercentage = "Pass percentage is required";
        } else if (passPercentage < 35 || passPercentage > 100) {
            newErrors.passPercentage = "Enter a valid pass percentage between 35 and 100";
        }



        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const sensors = useSensors(useSensor(PointerSensor));

   useEffect(() => {
    if (formData.quiz_id) {
        fetchQuestions();
    }
}, [formData.quiz_id]);

const fetchQuestions = async () => {
    try {
        const response = await quizService.getQuizQuestions(formData.quiz_id);
        if (response.data) {
            setQuizQuestions(response.data);
            console.log("Fetched quiz questions:", response.data);
        }
    } catch (error) {
        console.error("Error fetching updated questions:", error);
    }
};


const handleDragEnd = (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    setQuizQuestions((prevQuestions) => {
        const oldIndex = prevQuestions.findIndex((q) => q.id === active.id);
        const newIndex = prevQuestions.findIndex((q) => q.id === over.id);

        const newOrder = arrayMove(prevQuestions, oldIndex, newIndex);

        // Update the question_order field before sending to backend
        const reorderedPayload = newOrder.map((q, index) => ({
            id: q.id,
            order: index + 1
        }));

        // Save to backend
        quizService.saveQuestionOrder(id, reorderedPayload)
            .then((res) => {
                setSnackbar({
                    open: true,
                    message: res.data?.success ? 'Order updated successfully' : 'Failed to update order',
                    severity: res.data?.success ? 'success' : 'error',
                });
                   if (res.data?.success) {
            fetchQuestions(); // <-- Refresh questions from DB
        }
            })
            .catch(() => {
                setSnackbar({
                    open: true,
                    message: 'Error updating order',
                    severity: 'error'
                });
            });

        return newOrder; // Important: Update UI immediately
    });
};


    const handleSubmit = async (isDraft = false) => {
        if (!validateForm()) {
            setSnackbar({
                open: true,
                message: 'Please fill all required fields',
                severity: 'error'
            });
            return;
            
        }

        

        setLoading(true);
        try {
            const payload = {
                quiz_title: formData.quiz_title,
                description: formData.description || '',
                shuffle_answers: shuffleAnswers ? 1 : 0,
                random_questions: randomizeQuestions ? 1 : 0,
                time_limit_req: timeLimitEnabled ? 1 : 0,
                time_limit: timeLimitEnabled ? timeLimit : 0,
                mark_per_question: gradeType === 0 ? markPerQuestion : 0,
                grade_type: gradeType,
                show_ans: showAnswers ? '1' : '0',
                ans_pass_fail: showResultOption || '0',
                save: isDraft ? 'draft' : 'publish',
                pass_percentage: passPercentage,
                skills: formData.skills,
            };
            if (isEditMode) {
                payload.quiz_id = id;
            }

            const response = await quizService.createQuiz(payload);
            let newQuizId = isEditMode ? id : response.data.quizId;
            setFormData(prev => ({ ...prev, quiz_id: newQuizId }));
            //  Mark as saved
             setIsSaved(true);
            setSnackbar({
                open: true,
                message: `Quiz ${isEditMode ? 'updated' : 'created'} successfully!`,
                severity: 'success'
            });
            window.history.pushState({}, '', `/quiz/form/${newQuizId}?tab=1`);
            setActiveTab(1);
            setTabValue(1);
        }
        catch (error) {
            console.error('Error saving quiz:', error);
            setSnackbar({
                open: true,
                message: error.response?.data?.message || 'Error saving quiz',
                severity: 'error'
            });
        } finally {
            setLoading(false);
        }
    };
    function SortableQuestion({ id, question, onRemove }) {
        const {
            attributes,
            listeners,
            setNodeRef,
            transform,
            transition,
        } = useSortable({ id });

        const style = {
            transform: CSS.Transform.toString(transform),
            transition,
            p: 2,
            width: '100%',
            maxWidth: 600,
            position: 'relative',
            marginBottom: '16px',
        };

        return (
            <Paper ref={setNodeRef} sx={style} {...attributes} elevation={3}>
                {/* Drag Handle */}
                <IconButton
                    {...listeners}
                    sx={{
                        position: 'absolute',
                        top: 8,
                        left: 8,
                        cursor: 'move',
                    }}
                >
                    <DragIndicatorIcon fontSize="small" />
                </IconButton>

                {/* Remove Button */}
                <IconButton
                    aria-label="delete"
                    size="small"
                    onClick={() => onRemove(id)}
                    sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                    }}
                >

                    <CloseIcon fontSize="small" />
                </IconButton>

                <Typography variant="body1" sx={{ pl: 5, pr: 5 }}>
                    {question}
                </Typography>
            </Paper>
        );
    }

    if (fetchLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress />
            </Box>
        );
    }
    const handleStatus = async (id, isDraft = false) => {

        if (!isDraft && quizQuestions.length === 0) {
        setSnackbar({
            open: true,
            message: 'Please add at least one question before publishing the quiz',
            severity: 'error',
        });
        return;
    }
        setLoading(true);
        try {
            const payload = {
                type: isDraft ? 1 : 2,
                quiz_id: id
            };
            const response = await quizService.updateQuizStatus(payload);
            setSnackbar({
                open: true,
                message: `Status ${isDraft ? 'saved as Draft' : 'Published'} successfully!`,
                severity: 'success',
            });
            setTimeout(() => {
                navigate('/quiz');
            }, 1000);

        } catch (error) {
            console.error('Error saving Status:', error);
            setSnackbar({
                open: true,
                message: error.response?.data?.message || 'Error saving Status',
                severity: 'error',
            });
        } finally {
            setLoading(false);
        }
    };
    const handleRemoveQuestion = async (refid) => {
        try {
            const response = await quizService.deleteQuestion(refid);
            if (response.data?.success) {

                setQuizQuestions(prev => prev.filter(q => q.id !== refid));
                setSnackbar({ open: true, message: 'Question removed successfully', severity: 'success' });
            } else {
                setSnackbar({ open: true, message: 'Failed to remove the question', severity: 'error' });
            }
        } catch (error) {
            setSnackbar({ open: true, message: 'An error occurred while removing the question', severity: 'error' });
        }
    };
    return (
        <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <MuiAlert
                    severity={snackbar.severity}
                    variant="filled"
                    onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                >
                    {snackbar.message}
                </MuiAlert>
            </Snackbar>


            <Tabs
                value={activeTab}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
                <Tab label="Details" />
                <Tab label="Questions" />
            </Tabs>


            <Box sx={{ p: 3 }}>
                {tabComponents[activeTab]}
            </Box>
            {activeTab === 0 && (
                <>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, mt: 1 }}>
                        <Typography variant="h4">
                            {id ? 'Edit Quiz' : 'Create New Quiz'}
                        </Typography>
                        <Button
                            variant="outlined"
                            startIcon={<ArrowBackIcon />}
                            onClick={() => navigate('/quiz')}
                        >
                            Back to Quiz
                        </Button>
                    </Box>

                    <form onSubmit={(e) => { e.preventDefault(); handleSubmit(false); }}>
                        <FormSection>
                            <Typography variant="h6" sx={{ mb: 3 }}>
                                Basic Information
                            </Typography>
                            <TextField
                                fullWidth
                                label="Quiz Title"
                                name="quiz_title"
                                value={formData.quiz_title}
                                onChange={handleChange}
                                variant="outlined"
                                sx={{ mb: 3 }}
                                error={!!errors.quiz_title}
                                helperText={errors.quiz_title}
                                required
                            />
                        </FormSection>

                        <FormSection>
                            <Typography variant="h6" sx={{ mb: 3 }}>
                                Quiz Description
                            </Typography>
                            <ReactQuill
                                theme="snow"
                                value={formData.description}
                                onChange={handleDescriptionChange}
                                style={{ height: '200px', marginBottom: '50px' }}
                                modules={modules}
                            />
                        </FormSection>

                        <FormSection>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={showAnswers}
                                        onChange={(e) => setShowAnswers(e.target.checked)}
                                    />
                                }
                                label={
                                    <Typography fontWeight="bold">
                                        Show Correct Answer to Learners after Quiz
                                    </Typography>
                                }
                            />
                            {showAnswers && (
                                <Box sx={{ mt: 2 }}>
                                    <FormControl component="fieldset">
                                        <RadioGroup
                                            row
                                            value={showResultOption}
                                            onChange={(e) => setShowResultOption(e.target.value)}
                                        >
                                            <FormControlLabel value="1" control={<Radio />} label={
                                                <Typography fontSize="2">
                                                    Only if Learners pass the quiz.
                                                </Typography>
                                            } />
                                            <FormControlLabel value="2" control={<Radio />} label={
                                                <Typography fontSize="2">
                                                    Always show correct answers, even if Learners fail the quiz.
                                                </Typography>
                                            } />
                                        </RadioGroup>
                                    </FormControl>
                                </Box>
                            )}
                        </FormSection>
                        <FormSection mt={-2}>
                            <FormControl error={!!errors.gradeType}>
                                <RadioGroup
                                    row
                                    name="grade_type"
                                    value={gradeType}
                                    onChange={(e) => setGradeType(Number(e.target.value))}
                                >
                                    <FormControlLabel
                                        value={0}
                                        control={<Radio />}
                                        label={<Typography fontWeight="bold">Auto Grade</Typography>}
                                    />
                                    <FormControlLabel
                                        value={1}
                                        control={<Radio />}
                                        label={<Typography fontWeight="bold">Manual Grade</Typography>}
                                    />
                                </RadioGroup>
                                {errors.gradeType && (
                                    <FormHelperText>{errors.gradeType}</FormHelperText>
                                )}
                            </FormControl>

                            {gradeType === 0 && (
                                <TextField
                                    label="Point Per Question"
                                    type="number"
                                    inputProps={{ min: 1 }}
                                    value={markPerQuestion}
                                    onChange={(e) => setMarkPerQuestion(e.target.value)}
                                    required
                                    fullWidth
                                    margin="normal"
                                    error={!!errors.markPerQuestion}
                                    helperText={errors.markPerQuestion}
                                />
                            )}
                            <Box mt={2}>
                            <TextField
                            label="Pass Percentage"
                            type="number"
                            inputProps={{ min: 1, max: 100 }}
                            value={passPercentage}
                            onChange={(e) => setPassPercentage(e.target.value)}
                            required
                            fullWidth
                            margin="normal"
                            error={!!errors.passPercentage}
                            helperText={errors.passPercentage || "Set the minimum percentage required to pass."}
                            />
                            </Box>

                            <Divider sx={{ my: 2, borderColor: 'red' }} />
                        </FormSection>

                        <FormSection mt={-2}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={shuffleAnswers}
                                        onChange={(e) => setShuffleAnswers(e.target.checked)}
                                    />
                                }
                                label={<Typography fontWeight="bold">Shuffle Answers</Typography>}
                            />

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={randomizeQuestions}
                                        onChange={(e) => setRandomizeQuestion(e.target.checked)}
                                    />
                                }
                                label={<Typography fontWeight="bold">Randomize Questions</Typography>}
                            />
                        </FormSection>

                        <FormSection mt={-2}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={timeLimitEnabled}
                                        onChange={(e) => setTimeLimitEnabled(e.target.checked)}
                                    />
                                }
                                label={<Typography fontWeight="bold">Time Limit</Typography>}
                            />

                            {timeLimitEnabled && (
                                <>
                                    <TextField
                                        type="number"
                                        value={timeLimit}
                                        onChange={(e) => setTimeLimit(Math.max(1, Number(e.target.value)))}
                                        size="small"
                                        required={timeLimitEnabled}
                                        inputProps={{ min: 1 }}
                                        error={!!errors.timeLimit}
                                        helperText={errors.timeLimit}
                                        sx={{ width: '80px', ml: 4, mb: -2 }}
                                    />

                                    <Typography sx={{ display: 'inline', ml: 1 }}>Minutes</Typography>
                                </>
                            )}
                        </FormSection>

        <FormSection>
          <SkillsInput
            value={formData.skills}
            onChange={(newSkills) =>
              setFormData((prev) => ({
                ...prev,
                skills: newSkills,
              }))
            }
            error={!!errors.skills}
            helperText={errors.skills}
            required
          />
        </FormSection>
                        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                            <Button
                                variant="outlined"
                                onClick={() => navigate('/quiz')}
                                disabled={loading}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                startIcon={loading ? <CircularProgress size={24} color="inherit" /> : ''}
                                disabled={loading}
                                sx={{ px: 3, py: 1 }}
                            >
                                {loading ? 'Saving...' : 'Save and Add Question'}
                            </Button>

                        </Box>
                    </form>
                </>
            )}
            {activeTab === 1 && (
                <>
                    {/* Top Buttons */}
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap', mb: 3 }}>
                        <Typography
                            variant="body2"
                            color="primary"
                            sx={{ cursor: 'pointer', textDecoration: 'underline', fontSize: '1.2rem' }}
                            onClick={handleShow}
                        >
                            + Add Question
                        </Typography>

                        {/* <Button variant="outlined">Settings</Button>
                        <Button variant="outlined">Preview</Button> */}

                        <Button
                            variant="outlined"
                            onClick={() => handleStatus(formData.quiz_id, true)}
                            disabled={loading}
                        >
                            Save as Draft
                        </Button>

                        <Button
                            type="submit"
                            variant="contained"
                            onClick={() => handleStatus(formData.quiz_id, false)}
                            startIcon={loading ? <CircularProgress size={24} color="inherit" /> : null}
                            disabled={loading}
                        >
                            {loading ? 'Saving...' : 'Publish'}
                        </Button>
                    </Box>

                    {/* Added Questions Section */}
                   
                    {quizQuestions && quizQuestions.length > 0 && (

                        <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <Typography variant="h6" gutterBottom>
                                Added Questions
                            </Typography>
                            <DndContext
                                sensors={sensors}
                                collisionDetection={closestCenter}
                                onDragEnd={handleDragEnd}
                            >
                                <SortableContext
                                    items={quizQuestions.map((q) => q.id)}
                                    strategy={verticalListSortingStrategy}
                                >
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                        {quizQuestions.map((q) => (
                                            <SortableQuestion
                                                key={q.id}
                                                id={q.id}
                                                question={q.question}
                                                onRemove={handleRemoveQuestion}
                                            />
                                        ))}
                                    </Box>
                                </SortableContext>
                            </DndContext>
                        </Box>
                    )}
                    {/* Question Popup Dialog */}
                    <Dialog open={show} onClose={handleClose} maxWidth="md" fullWidth>
                        <DialogTitle
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                            }}
                        >
                            Questions List
                            <IconButton
                                edge="end"
                                color="inherit"
                                onClick={handleClose}
                                aria-label="close"
                            >
                                <CloseIcon />
                            </IconButton>
                        </DialogTitle>

                        <DialogContent sx={dialogContentStyle}>
                            <Box sx={{ display: 'flex', gap: 2, mb: 3, mt: 1 }}>
                                <QuestionPopupForm
                                quizId={formData.quiz_id}
                                    onClose={() => handleShow(false)}
                                    // onAddQuestions={(addedQuestions) => {
                                    //     setQuizQuestions((prev) => [...prev, ...addedQuestions]);
                                    //     handleShow(false);
                                    //     console.log("QuizId:",formData.quiz_id);
                                    // }}
                                    onAddQuestions={(addedQuestions) => {
                                    setQuizQuestions((prev) => {
                                        const existingIds = new Set(prev.map(q => q.id));
                                        const newUniqueQuestions = addedQuestions.filter(q => !existingIds.has(q.id));
                                        return [...prev, ...newUniqueQuestions];
                                    });
                                    handleShow(false);
                                    }}
                                />
                            </Box>
                        </DialogContent>
                    </Dialog>
                </>
            )}
        </Box >
    );
};


export default QuizForm;